<script setup lang="ts">
import type { KjdsDetailPageProps } from '@/types/appTypes'

const props = withDefaults(defineProps<KjdsDetailPageProps>(), {})

const app = useNuxtApp()
const storeIndex = useStoreIndex()
const storeArticleDetail = useStoreArticleDetail()

const baseTitle = '特集'
let headTitle = ''
let headDescription = ''

const refData = reactive({
  aTag: null, // タグリスト
  reportItems: [] as Article[],
  reportObjects: [] as ArticleObject[],
  presentItems: [],
})

const setHead = () => {
  const articleTitle = props.kjdsDetailData.article.title
  headTitle = `${articleTitle} | ${baseTitle}`
  headDescription = `${articleTitle}について。日本最大級のミュージアム情報サイト、アイエム［インターネットミュージアム］による特集記事です。`
  if (props.kjdsDetailData.article.description?.length) {
    headDescription = props.kjdsDetailData.article.description
  }
  // ぱんくず作成
  storeIndex.setBreadCrumbs([
    {
      text: baseTitle,
      disabled: false,
      to: { path: '/special' },
    },
    {
      text: articleTitle,
      disabled: true,
    },
  ])

  const headInput = app.$makeMeta({
    title: headTitle,
    description: headDescription,
    ogImage: props.kjdsDetailData.eyeCatch?.url ? props.kjdsDetailData.eyeCatch.url : undefined,
  })
  useHead(headInput)
}

// イベントから表示カラム作成
if (!refData.aTag) {
  refData.aTag = app.$getArticleTags(props.kjdsDetailData.article)
} // タグリスト

const initPage = async () => {
  // 特集に紐づく展覧会を取得
  const aEventItems = app.$getArticleRelationsMulti(
    props.kjdsDetailData.article,
    'event',
    null,
  )

  const map = new Map<string, Article>()

  const results = await Promise.all(
    (aEventItems ?? []).map(eventItem =>
      storeArticleDetail.fetchDetail({
        fetchParams: {
          articleTypeSlug: 'event',
          articleSlugOrId: eventItem.article_slug,
        },
        isNoThrowError: true,
      }),
    ),
  )

  // 取得したレポート詳細に紐づくプレゼントを抽出
  for (const res of results) {
    const reportDetail = res?.data?.article

    const presents = app.$getArticleRelationsMulti(reportDetail, 'present', null)
    presents.forEach(p => map.set(p.article_slug, p))
  }

  refData.presentItems = app.$sortArticleOpenDate([...map.values()], true)
}

await initPage()
setHead()
</script>

<template>
  <div
    v-if="props.kjdsDetailData"
    class="p-articleDetailPage"
  >
    <div class="l-carouselTitle">
      <SpecialDetailBanner
        v-bind:item="props.kjdsDetailData.article"
        v-bind:objects="props.kjdsDetailData.objects"
      />
    </div>
    <SpecialBody
      v-bind:item="props.kjdsDetailData.article"
      class="c-contentText"
    />

    <div class="c-link">
      <Clip
        class="c-link_clip"
        v-bind:mode="true"
        v-bind:article-type-slug="props.kjdsDetailData.article.article_type_slug"
        v-bind:article-slug="props.kjdsDetailData.article.article_slug"
        v-bind:count="props.kjdsDetailData.article.user_favorite_count"
      />
      <ShareButtons
        v-bind:title="headTitle"
      />
    </div>

    <ArticleTagList
      v-if="refData.aTag.length > 0"
      v-bind:article-name="props.kjdsDetailData.article.article_type_slug"
      v-bind:items="refData.aTag"
      class="pc-only c-specialList"
    />

    <RelationBox
      article="present"
      v-bind:header="'関連するプレゼント'"
      v-bind:articles="refData.presentItems"
    />
    <RelationBox
      article="report"
      v-bind:header="'関連するレポート'"
      v-bind:relations="props.kjdsDetailData.article.relations"
      v-bind:objects="props.kjdsDetailData.objects"
      sort-type="open_date"
      v-bind:sort-new="true"
    />
    <RelationBox
      article="event"
      v-bind:header="'関連する展覧会'"
      v-bind:relations="props.kjdsDetailData.article.relations"
    />
    <RelationBox
      article="museum"
      v-bind:header="'関連するミュージアム'"
      v-bind:relations="props.kjdsDetailData.article.relations"
    />
    <ArticleTagList
      v-if="refData.aTag.length > 0"
      v-bind:article-name="props.kjdsDetailData.article.article_type_slug"
      v-bind:items="refData.aTag"
      class="sp-only c-specialList"
    />
  </div>
</template>

<style lang="scss" scoped>
@use "@/assets/css/foundation/mixin/responsive";

.l-carouselTitle {
  position: absolute;

  // top: 50px;
  top: 0;
  width: 100%;

  @include responsive.mq_down(sp) {
    position: relative;
    top: 0;
    margin-bottom: 40px;
  }
}

.c-section-title {
  margin: 40px 0;
  padding: 0 0 19px;
  border-bottom: 1px solid var(--g_color_border_gray);
  color: var(--color_text_base);
  font-family: var(--g_font_family_KozukaGothicPr6N);
  font-size: 20px;
  font-weight: 500;
  line-height: 0.923;

  @include responsive.mq_down(sp) {
    display: flex;
    margin: 30px 0;
    padding: 0 0 9px;
    font-size: 18px;
    line-height: inherit;
  }

  &::before {
    content: "";
    display: inline-block;
    width: 26px;
    height: 26px;
    margin-top: -3px;
    margin-right: 5px;
    background-image: url("@/assets/images/icon-title.png");
    vertical-align: middle;

    @include responsive.mq_down(sp) {
      width: 20px;
      height: 20px;
      margin: 2px 5px 0 0;
      background-size: cover;
    }
  }
}

.c-contentText {
  @include responsive.mq_down(sp) {
    margin-bottom: 80px;
  }
}

.c-recommend {
  margin-top: 40px;
  margin-bottom: 50px;

  &_title {
    margin-top: 90px;
    padding-bottom: 15px;
    color: black;
    font-family: var(--g_font_family_SawarabiGothic);
    font-size: 20px;
    line-height: 1.6;
    text-align: left;
  }

  &_content {
    margin-top: 15px;
  }
}

.l-section {
  margin-top: 100px;

  @include responsive.mq_down(sp) {
    margin-top: 70px;
  }
}

.c-link {
  display: flex;
  justify-content: center;
  margin-top: 35px;

  @include responsive.mq_down(sp) {
    flex-direction: column;
    align-items: center;
  }

  > div {
    &:first-child {
      // margin-right: 75px;
      @include responsive.mq_down(sp) {
        margin: 0 0 20px;
      }
    }
  }
}
</style>
